import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Form } from 'react-bootstrap';
import { Formik, Form as FormikForm, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { GenericModal } from '../../../../components/common/Modal/GenericModal';
import { showErrorToast } from '../../../../components/PluginsMenu/Toastr/Toastr';
import user from "../../../../../images/no_user_placeholder.png";
import { genderOptions } from '../../../../../dummy';
import Select from 'react-select';
import LanguageSelect from '../../../student/profile/personal-details/LanguageSelect';
import CountryCodeSelect from "../../../../components/common/CountryCodeSelect";

const onlyLettersRegex = /^[A-Za-z\s]+$/;
const strictEmailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const educationRegex = /^[A-Za-z0-9\s().]+$/;
const TutorSchema = Yup.object({
  name: Yup.string().trim().matches(onlyLettersRegex, 'Tutor Name must only contain letters').required('Tutor Name is required'),
  gender: Yup.string().trim().required('Tutor Gender is required'),
  email: Yup.string().trim().matches(strictEmailRegex, 'Invalid email format').required('Tutor Email is required'),
  location: Yup.string().trim().required('Tutor location is required'),
  dialect: Yup.string().trim().required('Tutor language is required'),
  age: Yup.number().typeError('Age must be a number').min(12).max(80).required('Tutor age is required'),
  education: Yup.string().trim().matches(educationRegex, 'Invalid education format').required('Tutor education is required'),
  language: Yup.string().trim().required('Tutor language is required'),
  timezone: Yup.string().trim().required('Tutor timezone is required'),
  experienceLevel: Yup.number().typeError('Experience Level must be a number').min(0).max(50).required('Experience Level is required'),
  about: Yup.string().trim().max(500, 'About can be max 500 characters').required('About is required'),
  whatsappNumber: Yup.string()
    .test('phone-length', 'Phone number must be between 7 and 15 digits', (value) => {
      if (!value) return true; // Optional field
      const digitsOnly = value.replace(/\D/g, '');
      return digitsOnly.length >= 7 && digitsOnly.length <= 15;
    }),
});

export const EditTutorModal = ({ show, handleClose, tutor, handleSave, languages, learningLanguages }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const fileInputRef = useRef();
  const formikRef = useRef();

  // Parse phone number from tutor data
  const parsePhoneNumber = (whatsappNumber) => {
    if (!whatsappNumber) return { countryCode: '+234', whatsappNumber: '' };
    
    // Extract country code (assuming format like +234123456789)
    const match = whatsappNumber.match(/^(\+\d{1,4})(\d+)$/);
    if (match) {
      return {
        countryCode: match[1],
        whatsappNumber: match[2]
      };
    }
    
    return { countryCode: '+234', whatsappNumber: whatsappNumber.replace(/^\+\d{1,4}/, '') };
  };

  const initialPhoneData = parsePhoneNumber(tutor?.whatsappNumber);

  // Updated timezone options
  const timezoneOptions = useMemo(() => [
    { value: 'UTC−10', label: 'UTC−10  Pacific/Honolulu' },
    { value: 'UTC−09', label: 'UTC−09  America/Anchorage' },
    { value: 'UTC−08', label: 'UTC−08  America/Los_Angeles' },
    { value: 'UTC−07', label: 'UTC−07  America/Denver' },
    { value: 'UTC−06', label: 'UTC−06  America/Chicago' },
    { value: 'UTC−05', label: 'UTC−05  America/New_York' },
    { value: 'UTC−04', label: 'UTC−04  America/Barbados' },
    { value: 'UTC−03', label: 'UTC−03  America/Sao_Paulo' },
    { value: 'UTC−02', label: 'UTC−02  Atlantic/South_Georgia' },
    { value: 'UTC−01', label: 'UTC−01  Atlantic/Azores' },
    { value: 'UTC±00', label: 'UTC±00  Europe/London' },
    { value: 'UTC+01', label: 'UTC+01  Africa/Lagos' },
    { value: 'UTC+02', label: 'UTC+02  Africa/Cairo' },
    { value: 'UTC+03', label: 'UTC+03  Africa/Nairobi' },
    { value: 'UTC+03:30', label: 'UTC+03:30  Asia/Tehran' },
    { value: 'UTC+04', label: 'UTC+04  Asia/Dubai' },
    { value: 'UTC+04:30', label: 'UTC+04:30  Asia/Kabul' },
    { value: 'UTC+05', label: 'UTC+05  Asia/Karachi' },
    { value: 'UTC+05:30', label: 'UTC+05:30  Asia/Kolkata' },
    { value: 'UTC+05:45', label: 'UTC+05:45  Asia/Kathmandu' },
    { value: 'UTC+06', label: 'UTC+06  Asia/Dhaka' },
    { value: 'UTC+06:30', label: 'UTC+06:30  Asia/Yangon' },
    { value: 'UTC+07', label: 'UTC+07  Asia/Bangkok' },
    { value: 'UTC+08', label: 'UTC+08  Asia/Shanghai' },
    { value: 'UTC+09', label: 'UTC+09  Asia/Tokyo' },
    { value: 'UTC+09:30', label: 'UTC+09:30  Australia/Darwin' },
    { value: 'UTC+10', label: 'UTC+10  Australia/Sydney' },
    { value: 'UTC+11', label: 'UTC+11  Pacific/Guadalcanal' },
    { value: 'UTC+12', label: 'UTC+12  Pacific/Auckland' },
    { value: 'UTC+13', label: 'UTC+13  Pacific/Tongatapu' },
    { value: 'UTC+14', label: 'UTC+14  Pacific/Kiritimati' }
  ], []);

  useEffect(() => {
    if (show && tutor?.imagePath) {
      const fullImageUrl = `${process.env.REACT_APP_IMAGE_PATH}${tutor.imagePath}`;
      setPreviewUrl(fullImageUrl);
      setProfileImage(tutor.imagePath);
    }
  
    if (!show) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      setPreviewUrl(null);
      setProfileImage(null);
    }
  }, [show, tutor]);  

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setProfileImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const onSubmit = async (values, { setSubmitting }) => {
    try {
      const whatsappNumber = values.whatsappNumber;
      
      await handleSave({ 
        ...tutor,
        ...values, 
        whatsappNumber,
        profileImage 
      });
      formikRef.current.resetForm({ values });
      handleClose();
    } catch (err) {
      showErrorToast(err.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={{
        name: tutor?.name || '',
        gender: tutor?.gender || '',
        email: tutor?.email || '',
        location: tutor?.location || '',
        dialect: tutor?.dialectId || '',
        age: tutor?.age || '',
        education: tutor?.education || '',
        language: tutor?.languageId || '',
        timezone: tutor?.timezone || '',
        experienceLevel: tutor?.experienceLevel || '',
        about: tutor?.about || '',
        whatsappNumber: initialPhoneData.whatsappNumber,
      }}
      enableReinitialize
      validationSchema={TutorSchema}
      onSubmit={onSubmit}
    >
      {({ handleSubmit, values, setFieldValue }) => (
        <GenericModal
          show={show}
          handleClose={handleClose}
          title="Edit Tutor Profile"
          handleSave={() => formikRef.current.submitForm()}
        >
          <FormikForm onSubmit={handleSubmit}>
            <div className="text-center mb-4">
            <div className="author-profile text-center">
                <div
                    className="position-relative d-inline-block"
                    style={{ width: '130px', height: '130px' }}
                >
                    <img
                    src={previewUrl || user}
                    alt="user-image"
                    className="rounded-circle border"
                    height="130"
                    width="130"
                    style={{ objectFit: 'cover' }}
                    />
                    {/* Upload Button Overlay */}
                    <div
                    className="position-absolute d-flex justify-content-center align-items-center bg-dark"
                    style={{
                        bottom: '6px',
                        right: '6px',
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        cursor: 'pointer',
                        opacity: 0.8,
                    }}
                    onClick={() => fileInputRef.current?.click()}
                    title="Update Profile Picture"
                    >
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleImageChange}
                        accept="image/*"
                        style={{ display: 'none' }}
                    />
                    <i className="fa fa-camera text-white" style={{ fontSize: '14px' }} />
                    </div>
                </div>
            </div>
            </div>

            <Form className="row">
              {[
                ['name', 'Enter tutor name'],
                ['email', 'Enter tutor email'],
                ['location', 'Enter location'],
              ].map(([field, placeholder]) => (
                <div className="col-md-6 mb-3" key={field}>
                  <Form.Label>{field.charAt(0).toUpperCase() + field.slice(1)}</Form.Label>
                  <Field
                    as={Form.Control}
                    name={field}
                    type={field === 'email' ? 'email' : 'text'}
                    placeholder={placeholder}
                  />
                  <div className="text-danger"><ErrorMessage name={field} /></div>
                </div>
              ))}

              <div className="col-md-6 mb-3">
                <Form.Label>Also Speaks</Form.Label>
                  <Field name="dialect"
                    component={LanguageSelect}
                    options={languages}
                    placeholder="Select native language" />
                <div className="text-danger"><ErrorMessage name="dialect" /></div>
              </div>

              {['age', 'education'].map((field) => (
                <div className="col-md-6 mb-3" key={field}>
                  <Form.Label>
                    {field.charAt(0).toUpperCase() + field.slice(1)}
                  </Form.Label>
                  <Field
                    as={Form.Control}
                    name={field}
                    type={field === 'age' ? 'number' : 'text'}
                    placeholder={`Enter ${field}`}
                    min={field === 'age' ? 18 : undefined}
                    max={field === 'age' ? 100 : undefined}
                  />
                  <div className="text-danger"><ErrorMessage name={field} /></div>
                </div>
              ))}

              <div className="col-md-6 mb-3">
                <Form.Label>Teaches</Form.Label>
                  <Field name="language"
                    component={LanguageSelect}
                    options={learningLanguages}
                    placeholder="Select language" />         
                <div className="text-danger"><ErrorMessage name="language" /></div>
              </div>

              <div className="col-md-6 mb-3">
                <Form.Label>Gender</Form.Label>
                <Field as="select" name="gender" className="form-control">
                  {genderOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Field>
                <div className="text-danger"><ErrorMessage name="gender" /></div>
              </div>

              <div className="col-md-6 mb-3">
                <Form.Label>Timezone</Form.Label>
                <Select
                  inputId="timezone-select"
                  options={timezoneOptions}
                  value={timezoneOptions.find(opt => opt.label === values.timezone) || null}
                  onChange={(option) => setFieldValue('timezone', option ? option.label : '')}
                  placeholder="Select timezone"
                  isSearchable
                  menuPlacement="auto"
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: base => ({ ...base, zIndex: 9999 }),
                    menuList: base => ({ ...base, maxHeight: 200, overflowY: 'auto' }),
                    control: base => ({ ...base, minHeight: '44px' }),
                  }}
                />
                <div className="text-danger"><ErrorMessage name="timezone" /></div>
              </div>
              <div className="col-md-6 mb-3">
                <Form.Label>Experience Level (Years)</Form.Label>
                <Field as={Form.Control} name="experienceLevel" type="number"
                    placeholder="Enter experience" min={0} max={50} />
                <div className="text-danger"><ErrorMessage name="experienceLevel" /></div>
              </div>

              <div className="col-md-6 mb-3">
                <Form.Label>Phone Number</Form.Label>
                <div >
                  <div >
                    <CountryCodeSelect
                      value={values.whatsappNumber} // Full phone number with country code
                      onChange={(phone) => setFieldValue('whatsappNumber', phone)}
                    />
                  </div>
                </div>
              </div>

              <div className="col-md-12 mb-3">
                <Form.Label>About</Form.Label>
                <Field as="textarea" name="about" className="form-control"
                  placeholder="Write a short description about the tutor" rows={4} />
                <div className="text-danger"><ErrorMessage name="about" /></div>
              </div>
            </Form>
          </FormikForm>
        </GenericModal>
      )}
    </Formik>
  );
};